import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import config from "@/config.json";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isHomePage = location.pathname === "/";
  const isTransparent = isHomePage && !scrolled;

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "Catalogue", path: "/catalogue" },
    { label: "Contact Us", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHomePage
          ? "bg-[#FDFCF8]/95 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="navbar-logo"
            className={`font-heading text-2xl font-bold tracking-[0.15em] hover:text-[#C5A059] transition-colors duration-300 ${
              isTransparent ? "text-white" : "text-[#1A1A1A]"
            }`}
          >
            {config.business.name}
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-12" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300 py-1 ${
                  isActive(link.path)
                    ? "text-[#C5A059]"
                    : isTransparent
                    ? "text-white/80 hover:text-[#C5A059]"
                    : "text-[#1A1A1A] hover:text-[#C5A059]"
                }`}
              >
                {link.label}
                <span
                  className={`absolute bottom-0 left-0 h-[1px] bg-[#C5A059] transition-all duration-300 ${
                    isActive(link.path) ? "w-full" : "w-0"
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className={`md:hidden p-2 ${isTransparent ? "text-white" : "text-[#1A1A1A]"}`}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`md:hidden overflow-hidden transition-all duration-500 ease-in-out bg-[#FDFCF8]/98 backdrop-blur-md ${
          mobileOpen ? "max-h-64 border-t border-[#E5E5E5]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300 ${
                isActive(link.path)
                  ? "text-[#C5A059]"
                  : "text-[#1A1A1A] hover:text-[#C5A059]"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};
